import { useState, useEffect, useCallback, RefObject } from 'react';

interface SelectionInfo {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseTextSelectionReturn {
  selection: SelectionInfo | null;
  clearSelection: () => void;
}

export const useTextSelection = (containerRef: RefObject<HTMLElement>): UseTextSelectionReturn => {
  const [selection, setSelection] = useState<SelectionInfo | null>(null);

  const handleSelectionChange = useCallback(() => {
    const browserSelection = window.getSelection();
    
    if (!browserSelection || !containerRef.current || browserSelection.rangeCount === 0) {
      setSelection(null);
      return;
    }

    const selectedText = browserSelection.toString().trim();
    
    // Only show if there's selected text with minimum length
    if (selectedText.length < 3) {
      setSelection(null);
      return;
    }

    // Check if the selection is within our container
    const range = browserSelection.getRangeAt(0);
    const container = containerRef.current;
    
    // More robust container check - traverse up the DOM tree
    let isWithinContainer = false;
    let node: Node | null = range.commonAncestorContainer;
    while (node && node !== document) {
      if (node === container) {
        isWithinContainer = true;
        break;
      }
      node = node.parentNode;
    }
    
    if (!isWithinContainer) {
      setSelection(null);
      return;
    }

    // Get the bounding rect of the selection
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate position relative to the container
    const x = rect.left - containerRect.left + rect.width / 2;
    const y = rect.top - containerRect.top;
    
    console.log('Text selection detected:', {
      text: selectedText,
      x,
      y,
      containerRect,
      selectionRect: rect
    });
    
    setSelection({
      text: selectedText,
      x,
      y,
      width: rect.width,
      height: rect.height
    });
  }, [containerRef]);

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // Clear selection when clicking outside (but not on selection UI elements)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't clear if clicked on the container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      // Don't clear if clicked on selection button or dialog
      const clickedElement = target as Element;
      if (clickedElement && clickedElement.closest) {
        // Check if clicked on text selection button or dialog
        if (clickedElement.closest('[data-text-selection-ui]')) {
          return;
        }
      }
      
      clearSelection();
    };

    if (selection) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [selection, containerRef, clearSelection]);

  return {
    selection,
    clearSelection
  };
}; 